import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import { FormData, LogoData } from '../api/api';
import { allowedDraftField } from '../constants/formConstants';
import { useAxios } from '../context/AxiosContext';
import { transformIncomeData } from '../utils/incomingDataTransformationHelper';
import {
  combineFieldArrays,
  omitExtraFields,
  transformGroups,
  transformRecursively
} from '../utils/old_outcomingDataTransformationHelper';

const toBase64 = (file: any): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    // @ts-ignore
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const setLogo = async (logo: string | any[]): Promise<LogoData> => {
  const returnLogo: LogoData = {
    action: 'KEEP',
    image: ''
  };
  if (!logo || !Array.isArray(logo)) {
    returnLogo.action = 'DELETE';
  } else if (logo[0].preview && logo[0].size) {
    returnLogo.action = 'UPDATE';
    returnLogo.image = await toBase64(logo[0]);
  } else {
    returnLogo.action = 'KEEP';
  }

  return returnLogo;
};

export function useFormData({ type, id }: { type?: string; id?: string }) {
  const queryCache = useQueryClient();
  const { authAxios } = useAxios();
  const entity: string = type === 'draft' ? 'draft' : 'group';

  const fetchLogo = useCallback(
    async (id?: string) => {
      const response = await authAxios.get<FormData>(`/${entity}/${id}/logo`);
      if (response?.data?.file) {
        const string = 'data:image/jpeg;base64,' + response.data.file;
        return [{ preview: string }];
      }
    },
    [authAxios, entity]
  );

  const fetchFormData = useCallback(
    async (_: string, id?: string) => {
      const { data } = await authAxios.get<FormData>(`/${entity}/${id}`);
      const transformedData = transformIncomeData(data);
      transformedData.logo = await fetchLogo(id);
      return transformedData;
    },
    [authAxios, entity, fetchLogo]
  );

  const deleteFormData = useCallback(
    async ({ type, id }: { type: string; id: string | null }) => {
      const response = await authAxios.delete<FormData>(`/${type}/${id}`);
      return response.data;
    },
    [authAxios]
  );

  const updateFormData = useCallback(
    async (data) => {
      const { _id, ...updateData } = data;
      // transform outgoing data
      const combinedArrays = combineFieldArrays(updateData);
      const transformedRowsToArrayData = transformRecursively(combinedArrays);
      transformGroups(transformedRowsToArrayData);
      const preparedData = omitExtraFields(transformedRowsToArrayData, allowedDraftField);

      preparedData.logo = await setLogo(data.logo);
      const response = await authAxios.put<FormData>(`/${type}/${id}`, preparedData);
      return response.data;
    },
    [authAxios, id, type]
  );

  const insertFormData = useCallback(
    async ({ data, type }: { data: any; type: string }) => {
      // transform outgoing data
      const combinedArrays = combineFieldArrays(data);
      const transformedRowsToArrayData = transformRecursively(combinedArrays);
      transformGroups(transformedRowsToArrayData);
      transformedRowsToArrayData.logo = await setLogo(data.logo);

      const response = await authAxios.post<FormData>(`/${type}`, { ...transformedRowsToArrayData });
      return response.data;
    },
    [authAxios]
  );

  const { status, data, isFetching, dataUpdatedAt } = useQuery(['form', type, id], () => fetchFormData('form', id), {
    refetchOnWindowFocus: false,
    // `id` would be `null` at first (falsy),
    // so the query will not execute until the id exists
    enabled: Boolean(id && type)
  });

  const insertMutation = useMutation(insertFormData, {
    onSuccess: (data, variables) => {
      queryCache.invalidateQueries('form');
      queryCache.invalidateQueries('drafts');
      toast.success(`Successfully created ${data.name} #${data.planNumber}`, {
        position: 'top-center'
      });
    },
    onError: (error: any, variables) => {
      toast.error(`${error.response.data?.error}`, {
        position: 'top-center'
      });
    }
  });

  const updateMutation = useMutation(updateFormData, {
    onSuccess: (data, variables) => {
      const groupType = type === 'draft' ? 'drafts' : 'groups';
      queryCache.invalidateQueries('form');
      queryCache.invalidateQueries(groupType);
      toast.success('Data was updated');
    },
    onError: (error: any, variables) => {
      toast.error(error.message);
    }
  });

  const deleteMutation = useMutation(deleteFormData, {
    onSuccess: (data, variables) => {
      queryCache.invalidateQueries('drafts');
      toast.success('Data was deleted');
    },
    onError: (error: any, variables) => {
      toast.error(error.message);
    }
  });

  return {
    formData: data,
    /** React Query timestamp; bump forces DDF remount so lastUpdatedBy matches GET after refetch */
    formDataUpdatedAt: dataUpdatedAt,
    isFormFetching: isFetching,
    setLogo,
    removeFormData: deleteMutation.mutate,
    updateFormData: updateMutation.mutate,
    insertFormData: insertMutation.mutate,
    formStatus: status
  };
}
